import qs from 'qs';
import request from '@meitu/saas-editor-ui/es/request';
import { getInternalDesignerIdToken, getIntlClientID, isInternalDesignerMode } from '@/utils/auth';
import { getTextTemplateWebApiHostName, getWebApiHostname } from '@/utils/request';
import { Response } from '../index.d';
import {
    EditInternalDesignerTemplateParams,
    EditTemplateParams,
    FetchInternalDesignerTemplateInfoParams,
    FetchInternalDesignerTemplateInfoRespone,
    FetchTemplateInfoParams,
    FetchTemplateInfoRespone,
    TemplateCheckParams,
} from './index.d';

// 模版的默认dpi
let template_dpi = 72;

function adapterDPI(res: any) {
    const formula = res.data?.response?.formula;
    try {
        const resolution = JSON.parse(formula).resolution;
        if (resolution) {
            console.log('设置模版分辨率', resolution);
            template_dpi = resolution;
        }
    } catch (e) {
        console.error('设置模版分辨率失败', e);
    }
}

export function getTemplateDpi() {
    return template_dpi;
}

export function setTemplateDpi(dpi: number) {
    template_dpi = dpi;
}

export const fetchInternalDesignerTemplate = async function (params: FetchInternalDesignerTemplateInfoParams) {
    const data = await request<FetchInternalDesignerTemplateInfoRespone>('/seal_pie_internal/template_info.json', {
        method: 'GET',
        params,
    });

    adapterDPI(data);
    return data;
};

// 获取模板详情  https://api-mock.meitu-int.com/project/1594/interface/api/66752
export const fetchTemplateDetail = async function (params: FetchTemplateInfoParams) {
    if (isInternalDesignerMode()) {
        return await fetchInternalDesignerTemplate({
            ...params,
            id_token: getInternalDesignerIdToken() as string,
        });
    }
    const data = await request<FetchTemplateInfoRespone>('/seal_pie/template_info.json', {
        method: 'GET',
        params,
    });
    adapterDPI(data);
    return data;
};
// 编辑模板 https://api-mock.meitu-int.com/project/1594/interface/api/66738
export const editTemplate = async function (data: EditTemplateParams, query?: string) {
    if (isInternalDesignerMode()) {
        return await editInternalDesignerTemplate({
            ...data,
            id_token: getInternalDesignerIdToken() as string,
        });
    }

    return await request<Response<{}>>('/seal_pie/template_edit.json?' + query, {
        method: 'POST',
        data,
    });
};

export const createTemplate = async (data: any, query: any) => {
    const hasIntlClientID = getIntlClientID();
    return await request<Response<any>>('/seal_pie/template_add.json?' + query, {
        method: 'POST',
        data: hasIntlClientID
            ? {
                  ...data,
                  client_id: hasIntlClientID,
              }
            : data,
    });
};

export const saveDraftTemplate = async (data: any, query: any) => {
    return await request<Response<any>>('/seal_pie/template_save.json?' + query, {
        method: 'POST',
        data,
    });
};

export const createTextTemplate = async (data: any, query: any) => {
    const result = await request<Response<any>>(
        `${getTextTemplateWebApiHostName()}/pic_text/add?` + qs.stringify(query),
        {
            method: 'POST',
            data,
        },
    );

    return {
        data: {
            meta: {
                code: 0,
                msg: '',
                error: '',
                request_uri: '/seal_pie/formula_check',
            },
            response: {
                errors: [],
                data: result.data,
                id: (result.data as any).data.id,
            },
        },
    };
};

export const createWatermarkTemplate = async (data: any, query: any) => {
    return await request<Response<any>>(`${getTextTemplateWebApiHostName()}/watermark/create?` + qs.stringify(query), {
        method: 'POST',
        data,
    });
};

export const getWatermarkTemplate = async (params: FetchTemplateInfoParams) => {
    return await request<Response<any>>(`${getTextTemplateWebApiHostName()}/watermark/info?`, {
        method: 'GET',
        params,
    });
};

export const editWatermarkTemplate = async (data: any) => {
    return await request<Response<any>>(`${getTextTemplateWebApiHostName()}/watermark/update?`, {
        method: 'POST',
        data,
    });
};

export const fetchInternalDesignerTextTemplate = async function (params: FetchTemplateInfoParams) {
    return await request<FetchInternalDesignerTemplateInfoRespone>(`${getTextTemplateWebApiHostName()}/pic_text/info`, {
        method: 'GET',
        params,
    });
};

export const fetchTextTemplateDetail = async function (params: FetchTemplateInfoParams) {
    return await fetchInternalDesignerTextTemplate({
        ...params,
    });
};
// 编辑模板 https://api-mock.meitu-int.com/project/1594/interface/api/66738
export const editTextTemplate = async function (data: any) {
    return await editTextInternalDesignerTemplate({
        ...data,
    });
};

export const editTextInternalDesignerTemplate = async function (data: any) {
    return await request<Response<{}>>(`${getTextTemplateWebApiHostName()}/pic_text/edit`, {
        method: 'POST',
        data,
    });
};

// https://api-mock.meitu-int.com/mock/1289/seal_pie_internal/template_info.json
export const editInternalDesignerTemplate = async function (data: EditInternalDesignerTemplateParams) {
    return await request<Response<{}>>('/seal_pie_internal/template_edit.json', {
        method: 'POST',
        data,
    });
};

// // https://api-mock.meitu-int.com/project/1289/interface/api/116595
export const templateCheck = async function (data: TemplateCheckParams) {
    if (isInternalDesignerMode()) {
        return await internalDesignerTemplateCheck({
            ...data,
            id_token: getInternalDesignerIdToken() as string,
        });
    }

    const res = await request<Response<any>>('/seal_pie/formula_check.json', {
        method: 'POST',
        data,
    });

    if (res.data.meta?.code === 400) {
        return {
            errors: [res.data.meta.error],
        };
    }

    return res.data.response;
};

// https://api-mock.meitu-int.com/mock/1289/seal_pie_internal/template_info.json
export const internalDesignerTemplateCheck = async function (data: TemplateCheckParams) {
    const res = await request<any>('/seal_pie_internal/formula_check.json', {
        method: 'POST',
        data,
    });

    if (res.data.meta.code === 400) {
        return {
            errors: [res.data.meta.error],
        };
    }

    return res.data.response;
};

export const getTemplateQRcode = async (id: string) => {
    if (isInternalDesignerMode()) {
        const res = await request<any>('/seal_pie_internal/qr_code', {
            method: 'GET',
            params: {
                id,
            },
        });

        return res.data.response.data;
    }

    const res = await request<any>('/seal_pie/qr_code', {
        method: 'GET',
        params: {
            id,
        },
    });
    return res.data.response.data;
};

// https://api-mock.meitu-int.com/project/2110/interface/api/142127
export const fetchAITemplate = async (id: string) => {
    return await request<{ id: number; formula: string }>(`${getWebApiHostname()}/v1/ai_template/get`, {
        method: 'GET',
        params: { id },
    });
};

// https://api-mock.meitu-int.com/project/2110/interface/api/142127
export const updateAITemplate = async (data: { id: string; formula: string; preview: string }) => {
    return await request<any>(`${getWebApiHostname()}/v1/ai_template/save`, {
        method: 'POST',
        data,
    });
};
